import { ICategorized, ICategory } from './category';
import { IEquatable } from './entity';
import { ILayout } from './layout';
import { ILocalized } from './localized';
import { IRoute, IRouteLink, IRouteParams } from './route';

/**
 * Represents a listing item returned from the findMany method of a page collection.
 * @template T - The type of the categorized item.
 */
export type IPageItem<T extends ICategorized = ICategorized> = ILocalized<T> & {
  schema: string;
  category: ILocalized<ICategory> & { href: string };
  href: string;
  locale: string;
  market: string;
};

/**
 * Represents a detail item returned from the findOne method of a page collection.
 * @template T - The type of the categorized item.
 */
export type IPage<T extends ICategorized = ICategorized> = ILocalized<T> & {
  schema: string;
  alternates: IRoute[];
  breadcrumb: IRouteLink[];
  category: ILocalized<ICategory> & { href: string };
  href: string;
  locale: string;
  market: string;
  parentRoute?: IRouteLink;
  slug: string;
  title: string;
};

/**
 * Represents the props for a page component.
 * @template T - The type of the categorized item.
 */
export type PageProps<T extends ICategorized = ICategorized> = {
  layout: ILayout;
  page: IPage<T>;
  params: IRouteParams;
  locales?: string[];
  locale?: string;
  defaultLocale?: string;
};

/**
 * Represents the result of a page relation.
 * @template T - The type of the categorized item.
 */
export type IPageRelationResult<T extends ICategorized = ICategorized> = {
  href: string;
} & T;

/**
 * Represents a page relation.
 * @template T - The type of the categorized item.
 */
export type IPageRelation<T extends ICategorized = ICategorized> = {
  relationTo: string;
  value: IPageRelationResult<T> | IPageRelationResult<T>[];
};

/**
 * Represents an unresolved page relation.
 */
export type IPageUnresolvedRelation = {
  relationTo: string;
  value: IEquatable | IEquatable[];
};
